import React, { useEffect, useState } from "react";
import { Container, Box, createTheme, ThemeProvider, Button, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SectionHeader from "./components/SectionHeader";
import { getData } from "../../services/library.js";
import SurfaceCard from "./components/SurfaceCard.js";
import * as api from "./api.js";
import { preparar, facturar } from "./functions";
import Header from "./components/Header.js";
import RemitosTable from "./components/RemitosTable.js";
import NoHayRegistros from "./components/NoHayRegistros.js";
import { getUserBranchesFilter } from "../../utils/filters";
import ClientesAutocomplete from "./components/ClientesAutocomplete.js";
import PreFactura from "../ventas/facturacion/PreFactura.js";
import SucursalesAutocomplete from "./components/SucursalesAutocomplete";
import * as helpers from "./helpers";
import { useTableSelection } from "./hooks/useTableSelection";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import Items from "./components/Items.js";
import DatosFacturacion from "./components/DatosFacturacion.js";
import PreviewButton from "./components/PreviewButton.js";
import ClienteSeleccionado from "./components/ClienteSeleccionado.js";
import CustomBackdrop from "../CustomBackdrop.js";
import AlertaRemitosFacturacion from "./components/AlertaRemitosFacturacion.js";

const BRAND = {
  main: "#2471A3",
  dark: "#1B4F72",
  light: "#EAF2F8",
  accent: "#F39C12",
  surface: "#F4F7F9",
  border: "#E1E8ED",
};

function NuevaFacturacion(props) {
  const esEnvasado = props.origen === "envasado";

  const { rowSelection, setRowSelection } = useTableSelection(
    props.rowSelection || null
  );

  //Datos Base de referencia:
  const [clientes, setClientes] = useState();
  const [sucursales, setSucursales] = useState();
  const [remitosCliente, setRemitosCliente] = useState(
    props.pedidosCliente || null
  );
  const [densidadesKeyMap, setDensidadesKeyMap] = useState();
  const [sucursalesHabilitadas, setSucursalesHabilitadas] = useState(); //sucursales de las que el usuario podra ver los clientes

  //Datos necesarios para comprobante
  const [dataCliente, setDataCliente] = useState(props.dataCliente || null);
  const [remitosAFacturar, setRemitosAFacturar] = useState(null);

  const [leyenda, setLeyenda] = useState("");
  const [ocs, setOcs] = useState("");

  const unidadMedidaData = {
    id_um_facturacion: dataCliente?.id_um_facturacion,
    nombre: dataCliente?.descr_unidad,
    config: dataCliente?.um_config,
  };

  const [precioUnitario, setPrecioUnitario] = useState(0);
  const [sucEmiteFactura, setSucEmiteFactura] = useState();
  const [letra, setLetra] = useState(props.letra ?? null);

  const [remitosTexto, setRemitosTexto] = useState("");
  const [facturarDeshabilitado, setFacturarDeshabilitado] = useState(true);
  const [noHayFcA, setNoHayFcA]=useState(false);

  const keyMapRutas = {
    1: ["/pedidos/facturacion_especial/", "/remitos/a-facturar/cliente/1/"],
    2: ["/remitos/a-facturar/cliente/2/"],
    3: ["/remitos/a-facturar/cliente/3/"],
  };

  const [itemsForm, setItemsForm] = useState([]);
  const [regPrefactura, setRegPrefactura] = useState();

  //Manejadores componentes visuales
  const [mostrarPrefactura, setMostrarPrefactura] = useState();
  const [loadingRemitos, setLoadingRemitos] = useState(false);

  const userData = {
    sucursal: localStorage.getItem("sucursal"),
    sucursalAuth: localStorage.getItem("suc_auth"),
    esControlador: Boolean(Number(localStorage.getItem("controlador"))),
  };

  useEffect(() => {
    api.getSetSuc(userData, setSucursales, setSucEmiteFactura);
    const sucursales = getUserBranchesFilter();
    setSucursalesHabilitadas(sucursales);
    getData(`/empresas_densidades_keymap`, setDensidadesKeyMap);
  }, []);

  useEffect(() => {
    if (!esEnvasado || !dataCliente) return;
    api.getLetra(dataCliente.situacion_iva, setLetra);
    api.getRemitosEnvasado(
      dataCliente.id,
      dataCliente.id_empresa,
      setLoadingRemitos,
      setRemitosCliente
    );
  }, []);

  const textField = sucursales && (
    <SucursalesAutocomplete
      sucursales={sucursales}
      sucEmiteFactura={sucEmiteFactura}
      setSucEmiteFactura={setSucEmiteFactura}
    />
  );

  useEffect(() => {
    if (!Object.keys(rowSelection).length){
        setItemsForm([])
        return
      };

    const registrosSelected = Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => remitosCliente[parseInt(key)]);

    let allItems = [];

    if (!registrosSelected) return;
    if (!densidadesKeyMap) return;

    allItems = registrosSelected?.flatMap((remito) => {
      const itemsArr =
        typeof remito.items === "string"
          ? JSON.parse(remito.items)
          : remito.items || [];

      return itemsArr.map((item) => ({
        ...item,
        nro: remito.nro,
        id_remito: remito.id_remito,
        id_pedido: remito.id_pedido,
      }));
    });

    const itemsAgrupados = helpers.agruparYConvertirItems(
      allItems,
      dataCliente?.id_empresa,
      densidadesKeyMap,
      unidadMedidaData
    );

    setRemitosAFacturar(itemsAgrupados);
  }, [rowSelection, remitosCliente, dataCliente?.id_empresa, densidadesKeyMap]);

  const clean = () => {
    setRemitosCliente([]);
    setRowSelection({});
    setDataCliente(null);
    setRemitosAFacturar([]);
    setPrecioUnitario(0);
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: "#164e63",
      },
      secondary: {
        main: "#475569",
      },
      light: {
        main: "#f1f5f9",
      },
    },
  });

  const [loading, setLoading] = useState();

  const confirmar = () => {

    setLoading(true)
    preparar({
      cliente: dataCliente,
      direccion: remitosCliente[0]?.id_direccion_entrega?? null, 
      suc: sucEmiteFactura,
      precioUnitario,
      itemsForm,
      leyenda,
      ocs,
      remitosTexto,
      mostrarPrefactura: (a) => setMostrarPrefactura(a),
      setRegPrefactura: (a) => setRegPrefactura(a),
      setLoading: (value)=>setLoading(value)
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "90vh%",
          bgcolor: BRAND.surface,
          pt: { xs: 1, md: 2 },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, md: 4 }, mb: 5 }}
          disableGutters
        >
          {esEnvasado && (
            <Button
              onClick={props.close}
              startIcon={<ArrowBackIcon />}
              sx={{ mb: 1 }}
            >
              Volver
            </Button>
          )}
          {props.origen !== "listadoRemitos" && (
            <>
              <Header BRAND={BRAND} />
              <SurfaceCard sx={{ mb: 1 }} BRAND={BRAND}>
                <SectionHeader
                  step={1}
                  icon={<PersonSearchIcon />}
                  title={esEnvasado ? "Cliente" : "Seleccionar cliente"}
                  subtitle={
                    esEnvasado
                      ? "Cliente seleccionado desde entrega con remito."
                      : "Solo se listan clientes marcados como 'facturación especial' / 'factura con remito'."
                  }
                  BRAND={BRAND}
                />
                {esEnvasado ? (
                  <ClienteSeleccionado dataCliente={dataCliente} />
                ) : (
                  <ClientesAutocomplete
                    clientes={clientes}
                    setClientes={setClientes}
                    dataCliente={dataCliente}
                    setDataCliente={setDataCliente}
                    sucursales={sucursalesHabilitadas}
                    clean={clean}
                    keyMapRutas={keyMapRutas}
                    setRemitosCliente={setRemitosCliente}
                    setLoadingRemitos={setLoadingRemitos}
                    setLetra={setLetra}
                    setNoHayFcA={setNoHayFcA}
                  />
                )}
              </SurfaceCard>

              {remitosCliente?.length > 0 || esEnvasado ? (
                <>
                  <SurfaceCard
                    sx={{ mb: 1, p: { xs: 1.5, md: 2.5 } }}
                    BRAND={BRAND}
                  >
                    <Box sx={{ px: { xs: 0.5, md: 1 } }}>
                      <SectionHeader
                        step={2}
                        icon={<Inventory2OutlinedIcon />}
                        title="Remitos a facturar"
                        subtitle="Seleccioná los remitos del cliente que quieras facturar."
                        BRAND={BRAND}
                      />
                    </Box>
                    <Box
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        border: `1px solid ${BRAND.border}`,
                      }}
                    >
                      <RemitosTable
                        remitos={remitosCliente}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        setRemitosAFacturar={setRemitosAFacturar}
                        id_empresa={dataCliente.id_empresa}
                        densidadesKeyMap={densidadesKeyMap}
                      />
                    </Box>
                  </SurfaceCard>
                </>
              ) : (
                dataCliente && (
                  <AlertaRemitosFacturacion
                    clienteNombre={dataCliente?.razon_social}
                    loadingRemitos={loadingRemitos}
                    faltaFacturacion={noHayFcA}
                    onIrAPerfil={() => {
                      console.log("Ir a perfil del cliente");
                    }}
                  />
                  // <NoHayRegistros
                  //   clienteNombre={dataCliente?.razon_social}
                  //   loadingRemitos={loadingRemitos}
                  // />
                )
              )}
            </>
          )}
          {(dataCliente && remitosCliente?.length > 0) ||
          (esEnvasado && dataCliente) ? (
            <>
              {props.origen == "listadoRemitos" && (
                <DatosFacturacion
                  cliente={dataCliente}
                  selectorSucursal={textField}
                  letra={letra}
                  BRAND={BRAND}
                />
              )}
              <Items
                cliente={dataCliente}
                selectorSucursal={textField}
                remitosAFacturar={remitosAFacturar}
                sucFacturadora={sucEmiteFactura}
                leyenda={leyenda}
                setLeyenda={setLeyenda}
                ocs={ocs}
                setOcs={setOcs}
                letra={letra}
                remitosTexto={remitosTexto}
                setRemitosTexto={setRemitosTexto}
                itemsForm={itemsForm}
                setItemsForm={setItemsForm}
                onlyTable={props.origen === "listadoRemitos"}
                facturarDeshabilitado={facturarDeshabilitado}
                setFacturarDeshabilitado={setFacturarDeshabilitado}
              />
            </>
          ) : null}
          {(remitosCliente?.length > 0 || esEnvasado) && (
            <PreviewButton
              confirmar={confirmar}
              facturarDeshabilitado={facturarDeshabilitado}
            />
          )}
          {mostrarPrefactura && regPrefactura && (
            <PreFactura
              openPrefactura={mostrarPrefactura}
              regPrefactura={regPrefactura}
              closeNewInvoiceDialog
              closePrefactura={() => setMostrarPrefactura(false)}
              tipoComp={1}
            />
          )}
          {<CustomBackdrop 
            open={loading} 
            text={"Armando datos de prefactura..."}
          />}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default NuevaFacturacion;
